export type CareNavigationEncounterReviewScore = {
  rationale: string;
  value: number;
};

export type CareNavigationEncounterReviewFacts = {
  callsWithUnknownDuration: number;
  careplanGoalsCovered: number;
  careplanUpdated: boolean;
  connectedMinutes: number | null;
  followUpScheduled: boolean;
  linkedCallCount: number;
  meetsMinimumLength: boolean;
};

export type CareNavigationEncounterReviewRow = {
  callIds: string[];
  carespaceId: string;
  carespaceLink: string;
  encounterAt: string;
  encounterId: string;
  encounterType: string;
  facts: CareNavigationEncounterReviewFacts | null;
  feedback: string;
  isReviewed: boolean;
  needsReview: string[];
  navigatorName: string;
  promptName: string;
  promptVersion: number | null;
  reviewedAt: string;
  scores: Record<string, CareNavigationEncounterReviewScore>;
};

export type CareNavigationEncounterReviewsResponse = {
  cap: number;
  end: string;
  rows: CareNavigationEncounterReviewRow[];
  start: string;
};

export const CN_CALL_REVIEW_ROW_LIMITS = [50, 100, 150] as const;

export type CareNavigationEncounterReviewsParams = {
  end: string;
  encounterTypes?: string[];
  limit?: number;
  minLengthMinutes?: number;
  start: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isEncounterReviewFacts(value: unknown): value is CareNavigationEncounterReviewFacts {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.callsWithUnknownDuration) &&
    isFiniteNumber(value.careplanGoalsCovered) &&
    typeof value.careplanUpdated === "boolean" &&
    (value.connectedMinutes === null || isFiniteNumber(value.connectedMinutes)) &&
    typeof value.followUpScheduled === "boolean" &&
    isFiniteNumber(value.linkedCallCount) &&
    typeof value.meetsMinimumLength === "boolean"
  );
}

function isEncounterReviewScore(value: unknown): value is CareNavigationEncounterReviewScore {
  return isRecord(value) && typeof value.rationale === "string" && isFiniteNumber(value.value);
}

function isScores(value: unknown): value is Record<string, CareNavigationEncounterReviewScore> {
  return isRecord(value) && Object.values(value).every(isEncounterReviewScore);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isEncounterReviewRow(value: unknown): value is CareNavigationEncounterReviewRow {
  if (!isRecord(value)) return false;
  return (
    isStringArray(value.callIds) &&
    typeof value.carespaceId === "string" &&
    typeof value.carespaceLink === "string" &&
    typeof value.encounterAt === "string" &&
    typeof value.encounterId === "string" &&
    typeof value.encounterType === "string" &&
    (value.facts === null || isEncounterReviewFacts(value.facts)) &&
    typeof value.feedback === "string" &&
    typeof value.isReviewed === "boolean" &&
    isStringArray(value.needsReview) &&
    typeof value.navigatorName === "string" &&
    typeof value.promptName === "string" &&
    (value.promptVersion === null || isFiniteNumber(value.promptVersion)) &&
    typeof value.reviewedAt === "string" &&
    isScores(value.scores)
  );
}

export function isCareNavigationEncounterReviewsResponse(
  value: unknown,
): value is CareNavigationEncounterReviewsResponse {
  if (!isRecord(value)) return false;
  return (
    typeof value.start === "string" &&
    typeof value.end === "string" &&
    isFiniteNumber(value.cap) &&
    Array.isArray(value.rows) &&
    value.rows.every(isEncounterReviewRow)
  );
}
